import { FC, useContext, useState } from 'react';
import Modal from '@components/Modal';
import InputField from '@components/FormFields/InputField/InputField';
import Buttons from '@components/Buttons/Buttons';
import Spacer from '@components/Spacer';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { DocumentAccessContext } from '@providers/DocumentAccess';
import { useUser } from '@/hooks';

import s from './RejectDocumentModal.module.scss';

type Props = {
  onClose: () => void;
  documentRequestUserId: string;
  fileId: string;
  refetchDocument: () => Promise<void>;
  triggerOnReject?: (reason:string) => Promise<void>;
};

const RejectDocumentModal: FC<Props> = ({ triggerOnReject, onClose, documentRequestUserId, fileId, refetchDocument }) => {
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  const { accessToken } = useContext(DocumentAccessContext);
  const [user] = useUser();

  const { getData: rejectFileFn } = useNoodleApi(tsClient.documentRequests.rejectUploadedFile);
  const { getData: updateFileFn } = useNoodleApi(tsClient.documentRequests.updateDocumentRequestUserFile);

  if (!accessToken && !user?.id) {
    return null;
  }

  const rejectFile = async (): Promise<void> => {
    setIsRejecting(true);
    await rejectFileFn({
      accessToken: accessToken || '',
      fileId,
      id: documentRequestUserId,
      rejectionMessage,
    });
    await updateFileFn({
      fileId,
      id: documentRequestUserId,
      isRequired: true,
    });
    if(triggerOnReject) {
      triggerOnReject(rejectionMessage);
    }
    await refetchDocument();
    onClose();
  };

  return (
    <Modal
      hideBottomInset
      title='Reject Document'
      onClose={onClose}
    >
      <div className={s.container}>
        <InputField
          id='rejectMessage'
          name='rejectMessage'
          label='Rejection Message'
          onChange={(val) => setRejectionMessage(val)}
          values={{ rejectionMessage }}
          rows={3}
        />
        <Spacer size='24px' />
        <Buttons
          isFetching={isRejecting}
          disabled={!rejectionMessage}
          isSecondary
          isFullWidth
          onClick={rejectFile}
        >
          Reject File
        </Buttons>
      </div>
    </Modal>
  );
};

export default RejectDocumentModal;
